import type { Panel } from '../panel.class';
import type { PanelItemGroup } from '../../panel-item-groups/panel-item-group.class';
import { Vertex3 } from '../../../spatial-classes';
import { getPanelOverlapCfn } from './panel-space.cfn';
import { isDistributableGroupCfn } from './panel-layout-input.cfn';

export interface FineTuneGroupPositionParams {
	groupId: string;
	/** Wanted `center.x`; clamped so the column stays between its neighbours and inside the usable run */
	x: number;
}

export interface FineTuneGap {
	/** Nearest column on each side by current position, null at the panel's end */
	left: PanelItemGroup | null;
	right: PanelItemGroup | null;
	/** Smallest and largest `center.x` the column can take without overlapping either */
	minX: number;
	maxX: number;
}

/**
 * The room a column has between its neighbours (or the panel's usable ends):
 * the range of centre positions that keeps it off both.
 */
export const getFineTuneGapCfn = (panel: Panel, group: PanelItemGroup): FineTuneGap => {
	const overlap = getPanelOverlapCfn(panel);
	const halfPanel = panel.size.width / 2;
	const halfWidth = group.maxOutsideWidth / 2;
	const others = panel.panelItemGroups.children.filter((g) => g.id !== group.id && isDistributableGroupCfn(g));
	const left = others.filter((g) => g.center.x < group.center.x).sort((a, b) => b.center.x - a.center.x)[0] ?? null;
	const right = others.filter((g) => g.center.x > group.center.x).sort((a, b) => a.center.x - b.center.x)[0] ?? null;
	const leftEdge = left ? left.center.x + (left.maxOutsideWidth / 2) : -halfPanel + overlap.left;
	const rightEdge = right ? right.center.x - (right.maxOutsideWidth / 2) : halfPanel - overlap.right;
	let minX = leftEdge + halfWidth;
	let maxX = rightEdge - halfWidth;
	if (minX > maxX) {
		const mid = (minX + maxX) / 2;
		minX = mid;
		maxX = mid;
	}
	return { left, right, minX, maxX };
};

/**
 * Puts one column exactly where the customer dropped it, between the same two
 * neighbours, and marks it fine-tuned so the distributor keeps it there (it
 * reads as position-locked to the planner). No re-spacing happens here; a
 * swap, a reorder or an added appliance resets every fine-tune on the panel
 * and re-spaces the run (`Panel.resetFineTune`).
 */
export const fineTuneGroupPositionCfn = (panel: Panel, { groupId, x }: FineTuneGroupPositionParams): FineTuneGap => {
	const group = panel.panelItemGroups.children.find((g) => g.id === groupId);
	if (!group) throw new Error(`fineTuneGroupPosition: group '${groupId}' is not on panel '${panel.id}'.`);
	if (group.positionLocked) throw new Error(`fineTuneGroupPosition: group '${groupId}' is position-locked.`);

	const gap = getFineTuneGapCfn(panel, group);
	const clamped = Math.min(gap.maxX, Math.max(gap.minX, x));
	group.fineTuned = true;
	group.center = new Vertex3({ dataIn: { x: clamped }});
	return gap;
};

/** Forget every fine-tuned position on the panel; the next distribute re-spaces the whole run. */
export const resetFineTuneCfn = (panel: Panel): void => {
	for (const group of panel.panelItemGroups.children) {
		if (group.fineTuned) group.fineTuned = false;
	}
};
